import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import TableService from 'src/shared/api/table/TableService';
import { AuditUserInfo, CellComment, CellHistoryItem } from 'src/shared/api/table/types';
import { setError, setSuccess } from 'src/shared/lib/utils';
import { Button, Modal } from 'src/shared/ui';
import type { TableCellMetaMode, TableCellMetaTarget } from 'src/feature/Table/hooks/useContextMenu';

interface TableCellMetaModalProps {
  onClose: () => void;
  tableId: number;
  target: TableCellMetaTarget;
  initialMode: TableCellMetaMode;
}

const formatDate = (value: string): string => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }
  return parsedDate.toLocaleString('ru-RU');
};

const formatOperation = (operation: string): string => {
  const normalized = operation.trim().toLowerCase();
  if (normalized === 'create') return 'Создание';
  if (normalized === 'update') return 'Изменение';
  if (normalized === 'delete') return 'Удаление';
  return operation;
};

const formatActor = (user?: AuditUserInfo | null, fallbackId?: number | null): string => {
  const normalizedDisplayName = user?.display_name?.trim();
  if (normalizedDisplayName) {
    return normalizedDisplayName;
  }

  const normalizedFullName = user?.full_name?.trim();
  if (normalizedFullName) {
    return normalizedFullName;
  }

  const normalizedUserName = user?.user_name?.trim();
  if (normalizedUserName) {
    return normalizedUserName;
  }

  const normalizedEmail = user?.email?.trim();
  if (normalizedEmail) {
    return normalizedEmail;
  }

  const resolvedId = user?.id ?? fallbackId;
  if (typeof resolvedId === 'number' && resolvedId > 0) {
    return `ID ${resolvedId}`;
  }

  return 'Неизвестный пользователь';
};

const shouldShowUpdatedMeta = (comment: CellComment): boolean => {
  const createdActorId = comment.created_by_user?.id ?? comment.created_by;
  const updatedActorId = comment.updated_by_user?.id ?? comment.updated_by;

  return (
    createdActorId !== updatedActorId ||
    comment.created_at !== comment.updated_at ||
    formatActor(comment.created_by_user, comment.created_by) !==
      formatActor(comment.updated_by_user, comment.updated_by)
  );
};

export const TableCellMetaModal: FC<TableCellMetaModalProps> = ({
  onClose,
  tableId,
  target,
  initialMode,
}) => {
  const [mode, setMode] = useState<TableCellMetaMode>(initialMode);
  const [comments, setComments] = useState<CellComment[]>([]);
  const [history, setHistory] = useState<CellHistoryItem[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentValue, setEditingCommentValue] = useState('');

  const contextLabel = useMemo(
    () => `Строка ID ${target.rowId}, колонка "${target.columnName}"`,
    [target.columnName, target.rowId],
  );

  const loadComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const { data } = await TableService.getCellComments(tableId, {
        row_id: target.rowId,
        column_name: target.columnName,
      });
      setComments(data || []);
    } catch (error) {
      console.error('Не удалось загрузить комментарии', error);
      setError('Не удалось загрузить комментарии');
    } finally {
      setCommentsLoading(false);
    }
  }, [tableId, target.columnName, target.rowId]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await TableService.getCellHistory(tableId, {
        row_id: target.rowId,
        column_name: target.columnName,
        limit: 100,
      });
      setHistory(data || []);
    } catch (error) {
      console.error('Не удалось загрузить историю ячейки', error);
      setError('Не удалось загрузить историю ячейки');
    } finally {
      setHistoryLoading(false);
    }
  }, [tableId, target.columnName, target.rowId]);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode, target.columnName, target.rowId]);

  useEffect(() => {
    setNewComment('');
    setEditingCommentId(null);
    setEditingCommentValue('');
    void loadComments();
    void loadHistory();
  }, [loadComments, loadHistory]);

  const handleCreateComment = async () => {
    const commentValue = newComment.trim();
    if (!commentValue) {
      setError('Комментарий не может быть пустым');
      return;
    }

    setIsSubmitting(true);
    try {
      await TableService.createCellComment(tableId, {
        row_id: target.rowId,
        column_name: target.columnName,
        comment: commentValue,
      });
      setNewComment('');
      setSuccess('Комментарий сохранен');
      await loadComments();
    } catch (error) {
      console.error('Не удалось создать комментарий', error);
      setError('Не удалось сохранить комментарий');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('Удалить этот комментарий?')) {
      return;
    }

    setIsSubmitting(true);
    try {
      await TableService.deleteCellComment(tableId, commentId);
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
        setEditingCommentValue('');
      }
      setSuccess('Комментарий удален');
      await loadComments();
    } catch (error) {
      console.error('Не удалось удалить комментарий', error);
      setError('Не удалось удалить комментарий');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = (comment: CellComment) => {
    setEditingCommentId(comment.id);
    setEditingCommentValue(comment.comment);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingCommentValue('');
  };

  const handleUpdateComment = async () => {
    if (!editingCommentId) {
      return;
    }

    const nextValue = editingCommentValue.trim();
    if (!nextValue) {
      setError('Комментарий не может быть пустым');
      return;
    }

    setIsSubmitting(true);
    try {
      await TableService.updateCellComment(tableId, editingCommentId, { comment: nextValue });
      setEditingCommentId(null);
      setEditingCommentValue('');
      setSuccess('Комментарий обновлен');
      await loadComments();
    } catch (error) {
      console.error('Не удалось обновить комментарий', error);
      setError('Не удалось обновить комментарий');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="flex w-[60rem] max-w-[90dvw] flex-col">
        <div className="font-gothampro-500 mb-2 text-2xl text-green-100">Метаданные ячейки</div>
        <div className="font-gothampro-400 mb-5 text-sm dark:text-white">{contextLabel}</div>

        <div className="mb-5 flex gap-2">
          <Button
            size="small"
            classNames="w-40"
            variant={mode === 'comment' ? 'violet' : 'greyLight'}
            onClick={() => setMode('comment')}
          >
            Комментарии
          </Button>
          <Button
            size="small"
            classNames="w-40"
            variant={mode === 'history' ? 'violet' : 'greyLight'}
            onClick={() => setMode('history')}
          >
            История
          </Button>
          <Button
            size="small"
            classNames="ml-auto w-40"
            variant="greyLight"
            onClick={() => {
              if (mode === 'comment') {
                void loadComments();
                return;
              }
              void loadHistory();
            }}
            disabled={commentsLoading || historyLoading || isSubmitting}
          >
            Обновить
          </Button>
        </div>

        {mode === 'comment' && (
          <div className="flex flex-col">
            <textarea
              rows={4}
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Добавьте комментарий для этой ячейки"
              className="font-gothampro-400 mb-3 w-full rounded border border-violet-400 bg-white p-3 text-sm text-black transition-all placeholder:text-xs dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
            />
            <div className="mb-4 flex gap-2">
              <Button
                size="small"
                classNames="w-44"
                variant="green"
                onClick={handleCreateComment}
                disabled={isSubmitting || commentsLoading}
              >
                Сохранить комментарий
              </Button>
              <Button
                size="small"
                classNames="w-32"
                variant="greyLight"
                onClick={() => setNewComment('')}
                disabled={isSubmitting}
              >
                Очистить
              </Button>
            </div>

            <div className="max-h-[18rem] overflow-y-auto rounded border border-grey-1000 p-2">
              {commentsLoading && (
                <div className="font-gothampro-400 p-2 text-sm opacity-70 dark:text-white">
                  Загрузка комментариев...
                </div>
              )}
              {!commentsLoading && !comments.length && (
                <div className="font-gothampro-400 p-2 text-sm opacity-70 dark:text-white">
                  Комментариев пока нет
                </div>
              )}
              {!commentsLoading &&
                comments.map(comment => (
                  <div key={comment.id} className="mb-2 rounded border border-grey-1000 p-3">
                    {editingCommentId === comment.id ? (
                      <>
                        <textarea
                          rows={3}
                          value={editingCommentValue}
                          onChange={e => setEditingCommentValue(e.target.value)}
                          className="font-gothampro-400 mb-2 w-full rounded border border-violet-400 bg-white p-2 text-sm text-black dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="small"
                            classNames="w-28"
                            variant="green"
                            onClick={handleUpdateComment}
                            disabled={isSubmitting}
                          >
                            Сохранить
                          </Button>
                          <Button
                            size="small"
                            classNames="w-28"
                            variant="greyLight"
                            onClick={handleCancelEdit}
                            disabled={isSubmitting}
                          >
                            Отмена
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-gothampro-400 mb-3 whitespace-pre-wrap text-sm dark:text-white">
                          {comment.comment}
                        </div>
                        <div className="mb-3 space-y-1 text-extra-xs opacity-70 dark:text-white">
                          <div>
                            Создал: {formatActor(comment.created_by_user, comment.created_by)},{' '}
                            {formatDate(comment.created_at)}
                          </div>
                          {shouldShowUpdatedMeta(comment) && (
                            <div>
                              Обновил: {formatActor(comment.updated_by_user, comment.updated_by)},{' '}
                              {formatDate(comment.updated_at)}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="small"
                            classNames="w-24"
                            variant="violetOutline"
                            onClick={() => handleStartEdit(comment)}
                            disabled={isSubmitting}
                          >
                            Изменить
                          </Button>
                          <Button
                            size="small"
                            classNames="w-24"
                            variant="greyLight"
                            onClick={() => void handleDeleteComment(comment.id)}
                            disabled={isSubmitting}
                          >
                            Удалить
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {mode === 'history' && (
          <div className="max-h-[28rem] overflow-y-auto rounded border border-grey-1000 p-2">
            {historyLoading && (
              <div className="font-gothampro-400 p-2 text-sm opacity-70 dark:text-white">
                Загрузка истории...
              </div>
            )}
            {!historyLoading && !history.length && (
              <div className="font-gothampro-400 p-2 text-sm opacity-70 dark:text-white">
                История пуста
              </div>
            )}
            {!historyLoading &&
              history.map(item => (
                <div key={item.id} className="mb-2 rounded border border-grey-1000 p-3">
                  <div className="mb-1 text-extra-xs opacity-60 dark:text-white">
                    {formatDate(item.changed_at)}
                  </div>
                  <div className="mb-1 text-extra-xs uppercase opacity-80 dark:text-white">
                    {formatOperation(item.operation)}
                  </div>
                  <div className="mb-2 text-extra-xs opacity-70 dark:text-white">
                    Пользователь: {formatActor(item.changed_by_user, item.changed_by)}
                  </div>
                  <div className="font-gothampro-400 text-sm dark:text-white">
                    <span className="opacity-70">Было:</span> {item.old_value || '-'}
                  </div>
                  <div className="font-gothampro-400 text-sm dark:text-white">
                    <span className="opacity-70">Стало:</span> {item.new_value || '-'}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </Modal>
  );
};
